import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RecipesApi } from '../api/resources';
import { ApiError } from '../api/client';
import { ImageLightbox } from '../components/ImageLightbox';
import type { Recipe } from '../api/types';
import { useToast } from '../context/ToastContext';
import { formatCOP, formatPercent } from '../utils/format';

const emptyForm = { code: '', name: '', description: '', laborCost: '', wastePercentage: '', imageFile: null as File | null };

export function Recipes() {
  const { notify } = useToast();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState<{ imageUrl: string; title: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const result = await RecipesApi.list({ search: search || undefined, isActive: statusFilter === 'all' ? undefined : statusFilter === 'active' });
      setRecipes(result.recipes);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las recetas.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const { recipe } = await RecipesApi.create({
        code: form.code,
        name: form.name,
        description: form.description || undefined,
        laborCost: form.laborCost ? Number(form.laborCost) : undefined,
        wastePercentage: form.wastePercentage ? Number(form.wastePercentage) : undefined,
      });
      if (form.imageFile) {
        try {
          await RecipesApi.uploadImage(recipe.id, form.imageFile);
          notify('Arreglo creado con su imagen. Ahora agrega sus componentes.', 'success');
        } catch (err) {
          notify(err instanceof ApiError ? `Arreglo creado, pero no se pudo cargar la imagen: ${err.message}` : 'Arreglo creado, pero no se pudo cargar la imagen.', 'error');
        }
      } else {
        notify('Arreglo creado. Ahora agrega sus componentes.', 'success');
      }
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible crear la receta.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(r: Recipe) {
    try {
      if (r.isActive) {
        if (!window.confirm(`¿Desactivar el arreglo "${r.name}"?`)) return;
        await RecipesApi.deactivate(r.id);
      } else {
        await RecipesApi.activate(r.id);
      }
      notify('Estado actualizado.', 'success');
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar el estado.', 'error');
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Arreglos</h1>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cerrar' : '+ Nuevo arreglo'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <h2>Nuevo arreglo</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="rcode">Codigo</label>
                <input id="rcode" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="rname">Nombre</label>
                <input id="rname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="rlabor">Mano de obra (COP)</label>
                <input id="rlabor" type="number" min={0} step="0.01" value={form.laborCost} onChange={(e) => setForm({ ...form, laborCost: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="rwaste">Merma (%)</label>
                <input
                  id="rwaste"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  placeholder="Usa el valor global por defecto"
                  value={form.wastePercentage}
                  onChange={(e) => setForm({ ...form, wastePercentage: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="rimage">Imagen del arreglo (opcional)</label>
                <input
                  id="rimage"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] ?? null })}
                />
                <span className="hint">JPG, PNG o WebP. Máximo 5 MB.</span>
              </div>
            </div>
            <div className="field">
              <label htmlFor="rdesc">Descripcion</label>
              <textarea id="rdesc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Crear arreglo'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="form-grid" style={{ marginBottom: '1rem' }}>
          <div className="field">
            <label htmlFor="rsearch">Buscar</label>
            <input id="rsearch" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre o codigo..." />
          </div>
          <div className="field">
            <label htmlFor="rstatus">Estado</label>
            <select id="rstatus" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as never)}>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="all">Todos</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-block">Cargando arreglos...</div>
        ) : recipes.length === 0 ? (
          <p className="empty-state">No se encontraron arreglos.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Imagen</th>
                  <th>Codigo</th>
                  <th>Nombre</th>
                  <th className="text-right">Mano de obra</th>
                  <th className="text-right">Merma</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {recipes.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.imageUrl ? (
                        <button
                          type="button"
                          className="recipe-image-button"
                          onClick={() => setLightbox({ imageUrl: r.imageUrl!, title: r.name })}
                          aria-label={`Ampliar imagen de ${r.name}`}
                        >
                          <img className="recipe-list-thumb" src={r.imageUrl} alt={`Imagen de ${r.name}`} />
                        </button>
                      ) : <span className="text-muted">Sin imagen</span>}
                    </td>
                    <td>{r.code}</td>
                    <td>
                      <Link to={`/recetas/${r.id}`}>{r.name}</Link>
                    </td>
                    <td className="text-right">{formatCOP(r.laborCost)}</td>
                    <td className="text-right">{formatPercent(r.wastePercentage)}</td>
                    <td>
                      <span className={`pill ${r.isActive ? 'pill-active' : 'pill-inactive'}`}>{r.isActive ? 'Activo' : 'Inactivo'}</span>
                    </td>
                    <td>
                      <div className="actions-row">
                        <Link to={`/recetas/${r.id}`} className="btn btn-secondary btn-sm">
                          Ver / editar
                        </Link>
                        <button
                          type="button"
                          className={`btn btn-sm ${r.isActive ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => toggleActive(r)}
                        >
                          {r.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {lightbox && <ImageLightbox imageUrl={lightbox.imageUrl} title={lightbox.title} onClose={() => setLightbox(null)} />}
    </div>
  );
}
